import { useEffect, useMemo, useRef, useState } from "react";
import { client } from "../net/client";
import { cycleSpeed, speed as speedSig, speedName } from "../game/speed";
import { useSignal } from "../net/signal";
import { Hub } from "./Hub";
import { Profile } from "./Profile";
import { Shop } from "./Shop";
import { Room } from "./Room";
import { Watch } from "./Watch";
import { ChatBar, ChatLive, ChatPanel } from "./Chat";
import { chatOff } from "../game/chat";
import { HokmTable, HokmScorebar } from "../game/HokmTable";
import { CharBargTable, CharBargScorebar } from "../game/CharBargTable";
import { HaftTable, HaftScorebar } from "../game/HaftTable";
import { ShelemTable } from "../game/ShelemTable";
import { MaintenanceGate } from "./Maintenance";
import { loadTableArt } from "../game/tableArt";
import "../game/speed"; // ضریبِ سرعتِ صحنه را روی <html> می‌نویسد
import { carpets, isCarpet, showCarpet, thumbOf } from "../game/carpet";
import { fa } from "../game/cards";
import { Overlay } from "./Overlay";
import { useBackScreen } from "./screens";
import { authHref } from "./Account";
import { Dialogs, ask, askText, notify } from "./Dialog";
import { Cups } from "./Cups";
import { StakeIntro } from "../game/StakeIntro";
import { armIntro, planFor } from "../game/stake";

export function App() {
  // صفحه‌ی خانه یا فهرستِ تماشا. حالتِ محلی است و نه مسیرِ نشانی: تماشا جایی
  // نیست که بشود لینکش را فرستاد — میز چند دقیقه بعد تمام شده است.
  // ‎?watch=1 از سایت (دکمه‌ی «تماشا»ی نوارِ بالا) مستقیم به فهرستِ میزها می‌آید.
  // ‎?watch=… ، ‎?cups=1 و ‎?cup=<id> از لندینگ می‌آیند: لینکِ «جدول‌ها» و کارتِ
  // هر لیگ باید مستقیم همان صفحه را باز کند، نه خانه‌ای که باید دوباره از آن
  // گشت.
  const [page, setPage] = useState<"home" | "watch" | "cups" | "profile" | "shop">(() => {
    if (typeof location === "undefined") return "home";
    const q = new URLSearchParams(location.search);
    if (q.get("watch")) return "watch";
    if (q.get("cups") || q.get("cup")) return "cups";
    if (q.get("profile")) return "profile";
    // ‎?shop=1 از ویترینِ لندینگ می‌آید: کارتی که آن‌جا زده شده باید همین‌جا
    // باز شود و نه در لابی، وگرنه بازیکن باید دوباره دنبالش بگردد.
    if (q.get("shop")) return "shop";
    return "home";
  });
  const [chatPanel, setChatPanel] = useState(false);
  // شناسه‌ی کارزاری که لندینگ خواسته — یک بار، سرِ اولین رندر.
  const [initialCup] = useState(() =>
    typeof location === "undefined" ? null : new URLSearchParams(location.search).get("cup"));

  // اعلانِ سرور (پیامِ ادمین، پاسخِ پشتیبانی، اخطار): یک پیامِ گذرا روی صفحه؛
  // متنِ کامل در صندوق می‌مانَد.
  const lastNotice = useSignal(client.lastNotice);
  useEffect(() => { if (lastNotice) notify((lastNotice.kind === "warn" || lastNotice.kind === "maintenance" ? "⚠️ " : "✉️ ") + lastNotice.title); }, [lastNotice]);
  // خبرِ تعمیرات وسطِ بازی: نوارِ ماندگار، نه toastِ سه‌ثانیه‌ای و نه بیرون
  // انداختن. کسی که وسطِ دست است باید بتواند دستش را تمام کند و در همان حال
  // بداند که چه در راه است.
  const [maintSoon, setMaintSoon] = useState("");
  useEffect(() => {
    if (lastNotice?.kind === "maintenance") setMaintSoon(lastNotice.body || lastNotice.title || "سرویس به‌زودی برای به‌روزرسانی بسته می‌شود");
  }, [lastNotice]);

  // کدِ اتاق از خودِ نشانی: لینکی که دوستت می‌فرستد مستقیم سرِ همان اتاق می‌نشاند.
  // ولی نشستن ثبت‌نام می‌خواهد، پس کد تا وقتی حساب ثبت‌شده باشد نگه داشته
  // می‌شود؛ مهمان از دروازه‌ی لابی به لندینگ می‌رود و با همین نشانی برمی‌گردد.
  const pendingRoom = useRef<string | null>(null);
  const username = useSignal(client.username);
  const verified = useSignal(client.verified);
  const state = useSignal(client.state);
  useEffect(() => {
    void client.connect();
    loadTableArt();
    const code = new URLSearchParams(location.search).get("room");
    if (code && /^\d{6}$/.test(code)) pendingRoom.current = code;
  }, []);
  useEffect(() => {
    if (state !== "online" || !username || !verified || !pendingRoom.current) return;
    client.joinRoom(pendingRoom.current);
    pendingRoom.current = null;
  }, [state, username, verified]);

  // اتاقِ رمزدار: سرور که رمز خواست، همین‌جا پرسیده و دوباره فرستاده می‌شود.
  // در Hub نمی‌نشیند چون کدِ ‎?room= از لندینگ هم از همین راه می‌آید و آن
  // وقت هنوز هیچ صفحه‌ای باز نشده.
  const needPass = useSignal(client.needRoomPassword);
  useEffect(() => {
    if (!needPass) return;
    client.needRoomPassword.value = null;
    void (async () => {
      const pw = await askText({
        title: "این اتاق رمز دارد",
        body: "رمزی که میزبان گذاشته را بزن.",
        ok: "پیوستن",
        secret: true,
        placeholder: "رمزِ اتاق",
        maxLength: 64,
      });
      if (pw) client.joinRoom(needPass, pw);
    })();
  }, [needPass]);

  // ‎?watch=<شناسه‌ی میز> از لندینگ («همین حالا سرِ میز»): وصل که شدیم، تماشای همان
  // میز — بی‌حساب، چون تماشا آزاد است. «?watch=1» فقط فهرست را باز می‌کند.
  const pendingWatch = useRef<string | null>(
    (() => { const w = typeof location !== "undefined" ? new URLSearchParams(location.search).get("watch") : null; return w && w !== "1" ? w : null; })());
  useEffect(() => {
    if (state !== "online" || !pendingWatch.current) return;
    client.watchTable(pendingWatch.current);
    pendingWatch.current = null;
  }, [state]);

  const table = useSignal(client.table);
  const cb = useSignal(client.cbTable);
  const sh = useSignal(client.shTable);
  const hk = useSignal(client.hkTable);
  const room = useSignal(client.room);
  const watching = useSignal(client.watching);
  const myId = useSignal(client.playerId);
  const inGame = !!(table ?? cb ?? hk);

  /**
   * پرده‌ی شرطِ آغازِ میز.
   *
   * با شناسه‌ی میز کلید می‌خورد و نه با خودِ وضعیت: یک میز یک پرده دارد، و
   * هر وضعیتِ بعدیِ همان میز باید از کنارش رد شود. `armIntro` هم همین را
   * دوباره می‌سنجد، پس رندرِ دوباره و StrictMode بی‌خطرند.
   *
   * این‌جا و نه در خودِ میز، چون باید **پیش از** سوارشدنِ صحنه دروازه بسته
   * شده باشد؛ اثرهای فرزند بعد از رندرِ پدر اجرا می‌شوند و کنترلر همان‌جا
   * `introGate()` را می‌گیرد.
   */
  const tableId = table?.tableId ?? cb?.tableId ?? hk?.tableId ?? "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const intro = useMemo(() => armIntro(planFor(table ?? cb ?? hk)), [tableId]);
  // فرشِ میز از سرور می‌آید — با هر وضعیتِ تازه هم‌گام
  const carpetKey = table?.carpet ?? cb?.carpet ?? hk?.carpet ?? room?.carpet;
  useEffect(() => { showCarpet(carpetKey); }, [carpetKey]);

  // چت فقط سرِ میز معنا دارد — چه نشسته باشی چه تماشاچی. در اتاق هنوز میزی
  // نیست که پیام روی آن بنشیند.
  const chatOpen = table?.chatOpen ?? cb?.chatOpen ?? hk?.chatOpen ?? false;
  const isHost = table?.host ?? cb?.host ?? hk?.host ?? false;
  useEffect(() => { if (!inGame) setChatPanel(false); }, [inGame]);

  /**
   * «بازگشت»ِ گوشی، لایه به لایه.
   *
   * ترتیب همان ترتیبِ باز شدن است: برگه‌ی چت بعد از میز باز می‌شود، پس اول
   * بسته می‌شود. سرِ میز، بازگشت همان پرسشِ دکمه‌ی «بیرون» را می‌پرسد و اگر
   * نخواستی، ورودیِ تاریخچه دوباره گذاشته می‌شود و میز سرِ جایش می‌مانَد —
   * وگرنه یک اشاره‌ی لبه وسطِ دست، بازی را می‌بُرد.
   */
  useBackScreen(page !== "home", () => setPage("home"));
  useBackScreen(!!watching, () => client.stopWatch());
  useBackScreen(!watching && !!(inGame || room), () => {
    // پنجره‌ی خودمان ناهم‌زمان است؛ لایه می‌مانَد (true) و اگر «بیرون» گفت، می‌رویم.
    void leaveTable();
    return true;
  });
  // برگه‌ی چت «بازگشت» را از Overlay ِ خودش می‌گیرد؛ این‌جا دوباره ثبت نمی‌شود،
  // وگرنه دو ورودیِ تاریخچه می‌ماند و یک بازگشت دو لایه می‌بست.

  return (
    <div className="shell">
      {/* بالاتر از همه‌چیز: تا وقتی سرور بسته است، هیچ دکمه‌ای معنا ندارد. */}
      <MaintenanceGate />
      {maintSoon && (
        <div className="maintsoon" role="status">
          <span>{maintSoon}</span>
          <button type="button" onClick={() => setMaintSoon("")} aria-label="بستن">×</button>
        </div>
      )}
      <header className="hdr">
        {/* راهِ برگشت به صفحه‌ی معرفی. سرِ میز یا در اتاق پیوند نیست: یک لمسِ
            اشتباه نباید وسطِ دست بازیکن را از بازی بیرون ببرد — راهِ بیرون
            رفتن همان دکمه‌ی «بیرون» است که می‌پرسد. */}
        {inGame || room
          ? <h1>بازی وان</h1>
          : <h1><a href="/" title="صفحه‌ی بازی وان">بازی وان</a></h1>}
        <div className="spacer" />
        {room && !inGame && room.hostId === myId && <CarpetButton current={room.carpet} />}
        {inGame && <ChatButton open={chatPanel} onToggle={() => setChatPanel((v) => !v)} />}
        {(inGame || room) && <SpeedButton />}
        <FullscreenButton />
        {watching ? (
          // تماشاچی چیزی را رها نمی‌کند، پس پرسیدن هم ندارد.
          <button onClick={() => client.stopWatch()}>پایانِ تماشا</button>
        ) : inGame || room ? (
          <button onClick={() => void leaveTable()}>بیرون</button>
        ) : username ? (
          // ثبت‌شده: نامش و «خروج». تأییدنشده: به جای تغییرِ رمز، «تأییدِ ایمیل».
          // نامش، و بس. خروج و تغییرِ رمز و پشتیبانی همه در لندینگ‌اند:
          // این صفحه فقط برای چیدنِ میز و بازی است.
          verified
            ? <span className="who" dir="ltr" translate="no">@{username}</span>
            : <a className="hb cta" href={authHref("login")}>تأییدِ ایمیل</a>
        ) : (
          // مهمان: یک دکمه و نه دو تا. صفحه‌ی حساب خودش هر دو حالت را
          // دارد و همان‌جا می‌شود بینشان رفت، پس دو دکمه فقط جای نوار را
          // می‌گرفت — و روی گوشی جا تنگ است. لندینگ و صندوق از اول همین
          // یک دکمه را داشتند.
          <a className="hb cta" href={authHref("login")}>ورود / ثبت‌نام</a>
        )}
      </header>

      <ConnBar />
      <WatchBar watchers={table?.watchers ?? cb?.watchers ?? hk?.watchers ?? 0} watching={!!watching} />

      {table && <HokmScorebar state={table} />}
      {cb && <CharBargScorebar state={cb} />}
      {hk && <HaftScorebar state={hk} />}

      {table ? <HokmTable state={table} />
        : cb ? <CharBargTable state={cb} />
        : hk ? <HaftTable state={hk} />
        : sh ? <ShelemTable state={sh} />
        : room ? <Room room={room} />
        : page === "watch" ? <Watch onBack={() => setPage("home")} />
        : page === "cups" ? <Cups onBack={() => setPage("home")} initial={initialCup} />
        : page === "profile" ? <Profile onBack={() => setPage("home")} />
        : page === "shop" ? <Shop onBack={() => setPage("home")} />
        // «تماشا» و «لیگ و جام» از لابی برداشته شدند — جایشان لندینگ است و
        // همان‌جا هم غنی‌ترند. صفحه‌هایشان می‌مانند، چون لینک‌های لندینگ
        // (‎?watch=… ، ‎?cups=1 ، ‎?cup=…) مستقیم به همین‌جا می‌آیند.
        : <Hub onProfile={() => setPage("profile")} onShop={() => setPage("shop")} />}

      {/* جریانِ زنده‌ی پیام‌ها روی میز. تماشاچی نوارِ چت هم دارد، پس جریانش
          پایین‌تر می‌نشیند؛ بازیکن دستش آن‌جاست و جریان بالاتر می‌مانَد. */}
      {inGame && !chatPanel && <ChatLive raised={!!watching} />}
      {inGame && watching && !chatPanel && <ChatBar chatOpen={chatOpen} />}
      {inGame && (
        <ChatPanel open={chatPanel} onClose={() => setChatPanel(false)} chatOpen={chatOpen} host={isHost} />
      )}
      {intro && <StakeIntro key={intro.tableId} plan={intro} />}
      <KickedVeil />
      <Dialogs />
    </div>
  );
}

/** «بیرون» از میز یا اتاق — با پرسش، چون وسطِ دست برگشت‌ناپذیر است. */
async function leaveTable() {
  const yes = await ask({
    title: "از میز بیرون بروی؟",
    body: "جای تو تا مدتی ربات بازی می‌کند؛ اگر زود برگردی، سرِ همان میز می‌نشینی.",
    ok: "بیرون بروم",
    cancel: "می‌مانم",
    danger: true,
  });
  if (yes) client.leaveRoom();
}

/** انتخابِ فرشِ اتاق — فقط میزبان؛ انتخاب به سرور می‌رود و همه هم‌زمان می‌بینند. */
function CarpetButton({ current }: { current: string }) {
  const [open, setOpen] = useState(false);
  const cur = isCarpet(current) ? current : "eyvan";
  return (
    <>
      <button className="fs carpetbtn" onClick={() => setOpen(true)} title="فرشِ میز" aria-label="فرشِ میز">
        <img src={thumbOf(cur)} alt="" width={22} height={22} />
      </button>
      {open && (
        <Overlay label="فرشِ میز" className="carpetpick" onClose={() => setOpen(false)}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--gold-bright)" }}>فرشِ میز</div>
          <div className="swatches">
            {carpets.map((c) => (
              <button key={c.key} className={`swatch ${c.key === cur ? "on" : ""}`}
                      onClick={() => { client.setCarpet(c.key); setOpen(false); }} aria-label={c.fa}>
                <img src={thumbOf(c.key)} alt="" width={72} height={72} />
                <span>{c.fa}</span>
              </button>
            ))}
          </div>
          <button className="btn ghost" style={{ maxWidth: 220 }} onClick={() => setOpen(false)}>بستن</button>
        </Overlay>
      )}
    </>
  );
}

/**
 * تمام‌صفحه.
 *
 * روی گوشی، نوارِ نشانیِ مرورگر ته صفحه را می‌خورد — دقیقاً جایی که دستِ
 * بازیکن است. تمام‌صفحه آن نوار را می‌برد و میز همان قدِ اپ را می‌گیرد. iOS
 * سافاری این API را برای صفحه‌ی وب ندارد (فقط برای ویدئو)؛ آن‌جا دکمه
 * نمی‌آید تا دکمه‌ی بی‌کار نشان ندهیم.
 */
function FullscreenButton() {
  const [on, setOn] = useState(false);
  // fullscreenEnabled نه وجودِ تابع: قابِ بی‌اجازه (iframe بی‌allowfullscreen) و
  // سافاریِ iOS تابع را دارند ولی اجازه نه — دکمه‌ی بی‌کار نشان نمی‌دهیم.
  const can = typeof document !== "undefined" && document.fullscreenEnabled === true;
  useEffect(() => {
    if (!can) return;
    const sync = () => setOn(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [can]);
  if (!can) return null;
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch((e: unknown) => {
      // رد شد (بی‌اشاره‌ی کاربر، یا سیاستِ مرورگر) — بی‌صدا نمی‌گذریم، وگرنه دکمه «کار نمی‌کند» بی‌توضیح می‌مانَد
      console.warn("تمام‌صفحه رد شد:", e);
    });
  };
  return (
    <button className="fs" onClick={toggle} title={on ? "خروج از تمام‌صفحه" : "تمام‌صفحه"} aria-label={on ? "خروج از تمام‌صفحه" : "تمام‌صفحه"}>
      {/* SVG و نه «⤢»: آن نویسه از بلوکِ پیکان‌های کمیاب است و روی هر
          دستگاهی نیست — جایی مربعِ خالی می‌شد، جایی نازک و بی‌رنگ. */}
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {on
          ? <path d="M9 3v6H3M15 21v-6h6M3 15h6v6M21 9h-6V3" />
          : <path d="M3 9V3h6M21 15v6h-6M15 3h6v6M9 21H3v-6" />}
      </svg>
    </button>
  );
}

const stateLabel: Record<string, string> = {
  offline: "آفلاین",
  loggingIn: "در حالِ ورود…",
  connecting: "در حالِ اتصال…",
  online: "آنلاین",
  reconnecting: "در حالِ بازگشت…",
};

/**
 * صفحه‌ای که کنار گذاشته شده — روی میز، نه یک نوارِ باریک بالای صفحه.
 *
 * خرابیِ واقعی که این را آورد: تماشاچی میزی می‌دید که وسطِ یک دست یخ زده —
 * دو کارت روی میز که دیگر تکان نمی‌خوردند. میز کاملاً **زنده** به نظر
 * می‌رسید و فقط «متوقف»؛ آن نوارِ باریکِ بالا هم لابه‌لای امتیاز و نامِ
 * صندلی‌ها گم می‌شد. نتیجه این بود که آدم فکر می‌کرد بازی قفل کرده، نه
 * این‌که صفحه‌اش کنار گذاشته شده.
 *
 * و راهِ برگشت هم نداشت: تنها چاره تازه‌کردنِ صفحه بود. حالا یک دکمه هست —
 * با کلیکِ آدم و نه خودکار، چون آن یکی زبانه هم همین کار را می‌کند و دو تا
 * با هم بی‌پایان همدیگر را بیرون می‌اندازند.
 */
function KickedVeil() {
  const kicked = useSignal(client.kicked);
  if (!kicked) return null;
  return (
    <Overlay base="veil" label="این صفحه کنار گذاشته شد">
      <div className="box">
        <b>این صفحه کنار گذاشته شد</b>
        <p className="muted">
          همین حساب جای دیگری باز شد — یک زبانه‌ی دیگر، یا گوشی‌ات. هر حساب
          در هر لحظه فقط یک جا می‌تواند باشد، پس این صفحه دیگر خبری نمی‌گیرد.
        </p>
        <button className="btn" onClick={() => client.takeOver()}>
          همین‌جا ادامه بده
        </button>
        <p className="muted small">آن یکی صفحه آن‌وقت کنار گذاشته می‌شود.</p>
      </div>
    </Overlay>
  );
}

/** نوارِ وضعیت: حالتِ واقعی و آخرین خطا — نه یک «در حالِ اتصال»ِ همیشگی. */
function ConnBar() {
  const state = useSignal(client.state);
  const err = useSignal(client.lastError);
  if (state === "online" && !err) return null;
  const dot = state === "online" ? "online" : state === "offline" ? "off" : "busy";
  return (
    <div className="conn">
      <i className={`dot ${dot}`} />
      <span>{stateLabel[state]}</span>
      {err && <span className="err">— {err}</span>}
    </div>
  );
}

/**
 * دکمه‌ی چت، با شمارِ پیامِ نخوانده.
 *
 * «نخوانده» یعنی پیامی که وقتی برگه بسته بود رسید. نوارِ گذرا هم همان را
 * نشان می‌دهد، ولی محو می‌شود؛ این عدد می‌مانَد تا برگه باز شود.
 */
function ChatButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const msgs = useSignal(client.chat);
  const off = useSignal(chatOff);
  const seen = useRef(0);
  if (open || off) seen.current = msgs.length;
  // پیامِ پاک‌شده (میزِ تازه) نباید شمارنده را منفی کند.
  if (seen.current > msgs.length) seen.current = msgs.length;
  const unread = off ? 0 : msgs.length - seen.current;
  return (
    <button className="fs chatbtn" onClick={onToggle} title="چتِ میز" aria-label="چتِ میز">
      {/* اموجیِ رنگی کنارِ رابطِ طلایی وصله بود و اندازه‌اش هم دستِ قلمِ
          سیستم. SVG رنگ را از متن می‌گیرد و همه‌جا یک‌شکل است. */}
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.6-.7L3 21l1.9-5A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z" />
      </svg>
      {unread > 0 && <i className="badge">{fa(Math.min(unread, 99))}</i>}
    </button>
  );
}

/**
 * نوارِ تماشا — یک نوار، دو مخاطب.
 *
 * برای بازیکن: «دو نفر دارند نگاه می‌کنند». کسی که سرِ میزِ عمومی نشسته باید
 * بداند تماشا می‌شود، نه این‌که بعداً بفهمد.
 *
 * برای تماشاچی: «تماشا می‌کنی، دستِ کسی پیدا نیست» — تا سرِ اولین دست دنبالِ
 * کارت‌های خودش نگردد و فکر نکند بازی خراب است.
 */
function WatchBar({ watchers, watching }: { watchers: number; watching: boolean }) {
  if (!watching && watchers <= 0) return null;
  return (
    <div className="conn">
      <span aria-hidden>👁</span>
      {watching
        ? <span>تماشا می‌کنی — دستِ کسی پیدا نیست{watchers > 1 ? ` · ${fa(watchers)} تماشاچی` : ""}</span>
        : <span>{fa(watchers)} نفر تماشا می‌کنند</span>}
    </div>
  );
}

/**
 * دکمه‌ی سرعتِ صحنه — همان Speed.Cycle در اپ.
 *
 * در میز دیده می‌شود و نه در خانه: تنها جایی که معنا دارد همان‌جاست که
 * انیمیشن اجرا می‌شود.
 */
function SpeedButton() {
  useSignal(speedSig);
  return (
    <button onClick={cycleSpeed} title="سرعتِ صحنه">
      {speedName()}
    </button>
  );
}
